import type { EmailMessage, Quote, Reminder, Sample } from "../types/crm";
import { displayCustomerName } from "./utils";

export type NotificationKind = "reminder" | "sample" | "quote" | "payment" | "email";

export interface NotificationItem {
  id: string;
  kind: NotificationKind;
  title: string;
  detail: string;
  href: string;
  occurredAt: string;
  tone: "red" | "amber" | "blue" | "violet";
}

interface NotificationSources {
  reminders: Reminder[];
  samples: Sample[];
  quotes: Quote[];
  emails: EmailMessage[];
}

export function buildTopbarNotifications(
  { reminders, samples, quotes, emails }: NotificationSources,
  now = new Date(),
): NotificationItem[] {
  const endOfToday = new Date(now);
  endOfToday.setHours(23, 59, 59, 999);
  const sevenDaysFromNow = now.getTime() + 7 * 86_400_000;
  const items: NotificationItem[] = [];

  reminders
    .filter((item) => !item.completed_at && new Date(item.due_at).getTime() <= endOfToday.getTime())
    .forEach((item) => items.push({
      id: `reminder-${item.id}`,
      kind: "reminder",
      title: item.due_status === "overdue" ? `逾期：${item.title}` : `今日：${item.title}`,
      detail: item.customer ? displayCustomerName(item.customer) : "客户",
      href: `/customers/${item.customer_id}`,
      occurredAt: item.due_at,
      tone: item.due_status === "overdue" ? "red" : "amber",
    }));

  samples
    .filter((item) => ["delivered", "awaiting_customer_feedback", "feedback_received"].includes(item.status) && !item.customer_feedback && !item.test_result)
    .forEach((item) => items.push({
      id: `sample-${item.id}`,
      kind: "sample",
      title: `样品反馈：${item.product_name}`,
      detail: item.customer ? displayCustomerName(item.customer) : "客户样品",
      href: `/samples/${item.id}`,
      occurredAt: item.updated_at,
      tone: "violet",
    }));

  samples
    .filter((item) => !item.is_paid && (item.sample_fee || 0) > 0)
    .forEach((item) => items.push({
      id: `payment-${item.id}`,
      kind: "payment",
      title: `待付款：${item.product_name}`,
      detail: `${item.currency} ${Number(item.sample_fee).toLocaleString()}`,
      href: `/customers/${item.customer_id}`,
      occurredAt: item.updated_at,
      tone: "amber",
    }));

  quotes
    .filter((item) => item.status === "sent" && (!item.next_follow_up_at || new Date(item.next_follow_up_at).getTime() <= sevenDaysFromNow))
    .forEach((item) => items.push({
      id: `quote-${item.id}`,
      kind: "quote",
      title: `报价待跟进：${item.quote_no}`,
      detail: `${item.currency} ${Number(item.total_amount).toLocaleString()}`,
      href: `/customers/${item.customer_id}`,
      occurredAt: item.next_follow_up_at || item.updated_at,
      tone: "blue",
    }));

  emails
    .filter((item) => item.direction === "inbound" && !item.is_read)
    .forEach((item) => items.push({
      id: `email-${item.id}`,
      kind: "email",
      title: item.subject || "新邮件",
      detail: item.from_address,
      href: "/email",
      occurredAt: item.occurred_at,
      tone: "blue",
    }));

  return items
    .sort((left, right) => new Date(right.occurredAt).getTime() - new Date(left.occurredAt).getTime())
    .slice(0, 30);
}
